import { useCallback, useMemo } from 'react';
import { type OnboardingFunnelStep, trackOnboardingStep } from '@/analytics/onboardingFunnel';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import { useMergeOnboardingState, useResolvedConfig } from '@/hooks/queries/useConfigQueries';
import { useWebsiteSyncRunsQuery } from '@/hooks/queries/useWebsiteSyncQueries';

/**
 * Day-1 setup progress (IX-3929).
 *
 * The onboarding steps from the design. Completion is derived from real
 * config/crawl signals where they exist (brand, questions, CTAs);
 * "Test" and "Install" have no such signal, so they persist in the `onboarding`
 * config slug (`config.client_configs`, website scope) alongside `active` — the
 * flag set when the domain is claimed that marks it as onboarding. Established
 * domains have no override, so they read as live. Consumed by the Home Day-1
 * card and the sidebar footer, which hide once the site is live.
 */
export type SetupStepKey = 'brand' | 'starter' | 'cta' | 'test' | 'install';

/**
 * Onboarding flags set by the "Looks good" confirm on the enhanced review
 * screens. `<step>_reviewed` gates the matching step's `done` — presence of a
 * seeded config row is not enough; a human must confirm the screen.
 */
export type SetupReviewKey = 'brand_reviewed' | 'starter_reviewed' | 'cta_reviewed';

/** The subset of `onboarding` config that is app-written (see the schema). */
type OnboardingState = {
    active?: boolean;
    tested?: boolean;
    installed?: boolean;
    brand_reviewed?: boolean;
    starter_reviewed?: boolean;
    cta_reviewed?: boolean;
};

export interface SetupStep {
    id: SetupStepKey;
    /** Step title (design copy) — the one name for this step on every surface. */
    label: string;
    /** CTA button label (design copy). */
    cta: string;
    /** One-line explanation shown under the title. */
    lead: string;
    /** Route the CTA links to. */
    href: string;
    /** Not required to go live: skipped by the guided pass and by the counters. */
    optional?: boolean;
    done: boolean;
}

export interface SetupProgress {
    steps: SetupStep[];
    readyCount: number;
    readyTotal: number;
    readyPct: number;
    /** Title of the first unfinished step — the "what's next" line on the chip. */
    nextLabel: string;
    /**
     * Everything but the publish step is done: the client is ready to go live
     * rather than still setting up. Shared so the Home card and the sidebar
     * panel switch to the publish wording at the same moment.
     */
    readyToPublish: boolean;
    /**
     * The site is live: either the domain never onboarded through this flow
     * (no `onboarding.active`) or the Install/publish step is done. Drives
     * visibility of the Day-1 surfaces.
     */
    isLive: boolean;
    /** Whether the config-backed live/onboarding decision is still loading. */
    isLiveLoading: boolean;
    loading: boolean;
    /** Crawl is currently running (drives the Home ingest card). */
    crawlActive: boolean;
    /** Crawl has finished mapping the site at least once. */
    crawlMapped: boolean;
}

interface StepMeta {
    id: SetupStepKey;
    label: string;
    cta: string;
    lead: string;
    href: string;
    optional?: boolean;
}

const STEP_META: StepMeta[] = [
    {
        id: 'brand',
        label: 'Brand & tone',
        cta: 'Review brand',
        lead: 'Name, colors and voice — pulled from your site. Every agent reuses them.',
        href: '/config-studio/brand',
    },
    {
        id: 'starter',
        label: 'Starter questions',
        cta: 'Set up questions',
        lead: 'Starter questions that surface in the bar as visitors browse — one tap beats a blank box.',
        href: '/config-studio/website-agent/engagement',
    },
    {
        id: 'cta',
        label: 'Calls-to-action',
        cta: 'Configure CTAs',
        lead: 'The actions that convert — book a demo, start a trial, talk to sales, at the right moment.',
        href: '/config-studio/website-agent/ctas',
    },
    {
        id: 'test',
        label: 'Test your agent',
        cta: 'Open Playground',
        lead: "Try Rose on a sandbox copy before it's live. Ask what a buyer would and fix anything off.",
        href: '/config-studio/website-agent/playground',
    },
    {
        id: 'install',
        label: 'Put Rose on your site',
        cta: 'Get install code',
        lead: 'One snippet for your site. Nothing is live until you add it — this step publishes.',
        href: '/config-studio/website-agent/publish',
    },
];

export function useSetupProgress(): SetupProgress {
    const { selectedWorkspaceId } = useWorkspace();
    const runsQuery = useWebsiteSyncRunsQuery(selectedWorkspaceId);
    const configQuery = useResolvedConfig();

    return useMemo(() => {
        const runs = runsQuery.data ?? [];
        const onboarding: OnboardingState = configQuery.data?.resolver.onboarding ?? {};

        const crawlActive = runs.some((run) => run.status === 'queued' || run.status === 'mapping');
        const crawlMapped = runs.some((run) => run.status === 'mapped');

        // Review steps are done only once the user explicitly confirms the screen
        // (the "Looks good" button writes onboarding.*_reviewed). A seeded config
        // row — or a finished crawl — is a prerequisite the screen sets up, not
        // proof a human looked; the whole point of Day-1 review is forcing that
        // look before going live. Knowledge is no exception: a mapped crawl makes
        // the summary meaningful, but the client still has to check it and confirm.
        const derived: Record<SetupStepKey, boolean> = {
            // Brand & tone is pre-set during the signup wizard (tone preset +
            // detected brand color), so self-serve clients land with it already
            // done — Starter questions is their first real step (IX-4921). They
            // can still refine brand from the checklist or the playground.
            brand: onboarding.brand_reviewed === true || onboarding.active === true,
            starter: onboarding.starter_reviewed === true,
            cta: onboarding.cta_reviewed === true,
            test: onboarding.tested === true,
            install: onboarding.installed === true,
        };

        const steps: SetupStep[] = STEP_META.map((meta) => ({
            ...meta,
            done: derived[meta.id],
        }));

        const required = steps.filter((step) => !step.optional);
        const readyTotal = required.length;
        const readyCount = required.filter((step) => step.done).length;
        // 18% baseline from pre-filled data (design). The signup wizard already
        // collected four answers before the client lands here, so a literal
        // count/total would greet them with 0% for work they've done.
        const readyPct = readyTotal > 0 ? Math.round(18 + (readyCount / readyTotal) * 82) : 0;
        const firstTodo = required.find((step) => !step.done);
        const nextLabel = firstTodo ? firstTodo.label : 'Publish';
        // "install" is the publish step itself, so it is the only one allowed
        // to be outstanding here.
        const readyToPublish = steps.every(
            (step) => step.id === 'install' || step.optional || step.done
        );

        // Never onboarded through this flow (no `active`) → established → live.
        const isLive = onboarding.active !== true || onboarding.installed === true;

        return {
            steps,
            readyCount,
            readyTotal,
            readyPct,
            nextLabel,
            readyToPublish,
            isLive,
            // `data === undefined` covers the disabled query too: while the
            // domain is still resolving, TanStack v5 reports `isLoading` false
            // with no data, and the `onboarding = {}` fallback reads as live.
            // Treating that as "still loading" stops SidebarAutoBehavior from
            // latching a placeholder `isLive` before the real config arrives.
            isLiveLoading: configQuery.isLoading || configQuery.data === undefined,
            loading: runsQuery.isLoading || configQuery.isLoading,
            crawlActive,
            crawlMapped,
        };
    }, [runsQuery.data, runsQuery.isLoading, configQuery.data, configQuery.isLoading]);
}

/**
 * Persist onboarding state into the `onboarding` config slug. The database
 * merges the patch atomically so concurrent Test / Install marks cannot clobber
 * each other. Used to mark Test / Install done.
 */
export function useMarkOnboardingStep() {
    const merge = useMergeOnboardingState();
    const { selectedDomain } = useWorkspace();

    return useCallback(
        async (patch: OnboardingState, domain?: string) => {
            const targetDomain = domain ?? selectedDomain;
            if (!targetDomain) return;

            await merge.mutateAsync({
                domain: targetDomain,
                patch,
            });

            // Emit a funnel step for each gate this patch completes (IX-3897).
            for (const step of ONBOARDING_STEP_FLAGS) {
                if (patch[step] === true) trackOnboardingStep(step, targetDomain);
            }
        },
        [merge, selectedDomain]
    );
}

/** The `OnboardingState` flags that map 1:1 to a funnel step name. */
const ONBOARDING_STEP_FLAGS = [
    'starter_reviewed',
    'cta_reviewed',
    'tested',
    'installed',
] as const satisfies readonly (keyof OnboardingState & OnboardingFunnelStep)[];
